import { motion } from "framer-motion";
import { ChevronDown, Phone } from "lucide-react";
import { WA_LINK, scrollToId, useLang } from "../i18n";
import { WhatsAppLogo } from "./WhatsAppLogo";
import { Eyebrow } from "./Reveal";
import LangToggle from "./LangToggle";

function StatCard({ value, label, testid }) {
  return (
    <div
      data-testid={testid}
      className="min-w-[200px] rounded-2xl border border-white/15 bg-white/[0.06] px-7 py-5 backdrop-blur-sm"
    >
      <div className="price-oldstyle font-serif text-4xl text-gold">{value}</div>
      <div className="mt-1.5 text-[11px] font-bold tracking-[0.25em] text-white/85">
        {label}
      </div>
    </div>
  );
}

export default function Hero() {
  const { t } = useLang();
  return (
    <section
      id="home"
      className="relative flex min-h-screen items-center overflow-hidden bg-ink"
    >
      <div className="absolute inset-0" data-testid="hero-background">
        <img
          src="/assets/taxi-hero.png"
          alt="Professional taxi at night"
          className="h-full w-full object-cover"
          style={{ objectPosition: "center 45%" }}
        />
        <div className="absolute inset-0 bg-black/55"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/35 to-black/20"></div>
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-ink to-transparent"></div>
      </div>

      <div className="absolute left-1/2 top-[92px] z-20 -translate-x-1/2">
        <LangToggle />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 pb-24 pt-36 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <Eyebrow>{t.hero.eyebrow}</Eyebrow>
          <h1
            data-testid="hero-heading"
            className="mt-6 max-w-3xl font-serif text-[42px] leading-[1.06] text-white sm:text-6xl lg:text-[72px]"
          >
            {t.hero.titleA}{" "}
            <em className="italic text-gold">{t.hero.titleB}</em>{" "}
            {t.hero.titleC}
          </h1>
          <p className="mt-6 max-w-xl text-base text-white/85 sm:text-lg">
            {t.hero.sub}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-book-button"
              onClick={() => scrollToId("book")}
              className="inline-flex items-center gap-2.5 rounded-full bg-gold px-7 py-4 text-base font-extrabold text-black shadow-[0_10px_35px_rgba(217,165,68,0.35)] transition-all hover:-translate-y-0.5 hover:bg-gold-light"
            >
              <Phone className="h-5 w-5" />
              {t.hero.bookNow}
            </button>
            <a
              data-testid="hero-whatsapp-button"
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 rounded-full bg-wa px-7 py-4 text-base font-extrabold text-white shadow-[0_10px_35px_rgba(37,211,102,0.35)] transition-all hover:-translate-y-0.5 hover:brightness-110"
            >
              <WhatsAppLogo className="h-5 w-5" />
              {t.hero.waNow}
            </a>
          </div>

          <div className="mt-12 flex flex-wrap gap-4">
            <StatCard
              value={t.hero.stat1}
              label={t.hero.stat1label}
              testid="hero-stat-years"
            />
            <StatCard
              value={t.hero.stat2}
              label={t.hero.stat2label}
              testid="hero-stat-availability"
            />
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-7 right-28 z-10 hidden items-center gap-2 text-[11px] font-bold tracking-[0.3em] text-white/60 lg:flex">
        {t.hero.scroll}
        <ChevronDown className="h-4 w-4 animate-bounce" />
      </div>
    </section>
  );
}
